import { useFocusEffect } from '@react-navigation/core';
import { Button } from 'aimo-ui';
import PaymentCardDropDown from 'components/park/detail/PaymentCardDropDown';
import VehicleSelectDropDown from 'components/park/detail/VehicleSelectDropDown';
import {
  FooterButtonContainer,
  VerticalDistributedContainer,
} from 'components/sheets/SheetLayout';
import AccountContext from 'context/AccountContext';
import ParkingContext from 'context/ParkingContext';
import useReadPurchasedPrepaidTickets from 'hooks/useReadPurchasedPrepaidTickets';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import { isEmpty } from 'lodash';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from 'stores/authStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { usePermitStore } from 'stores/permitStore';
import { usePrepaidTicketStore } from 'stores/prepaidTicketStore';
import { SelectedSegment } from 'types/generatedSchemaTypes';
import { getCAbenefit } from 'utils/paymentUtils';

import {
  checkPrepaidOrPermitTicketDates,
  isItemPredefinedTicket,
} from 'utils/permitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { parseISO8601Duration } from 'utils/timeUtils';
import { useUiStore } from 'stores/uiStore';

const BuyPermitButton = () => {
  const { t } = useTranslation();
  const { licensePlate } = useSelectedParkingVehicle() || {};
  const paymentMethod = useSelectedPaymentMethod();
  const { parkingZoneResult: parkingZone, selectedParkzonePmcUnion } =
    useContext(ParkingContext);

  // We moved logic away from reactive vars and the context to zustand store
  // In the future, we will hopefully move permit purchase data to also its own store
  // But now we still use the "parking state" for this data
  const { parkingConditions, selectedParkingVehiclePlate } = useParkingStore(
    (state) => state
  );
  const { selectedParkingZoneId, parkingZoneCountryCode } =
    parkingConditions ?? {};

  const selectedVehicle = useSelectedParkingVehicle(
    false,
    undefined,
    selectedParkingVehiclePlate
  );
  const { purchasedPrepaidTickets } = useReadPurchasedPrepaidTickets({});
  const permitStartDate =
    usePermitStore((state) => state.selectedPermitStartDate) ?? new Date();
  const selectedSegment = useParkzoneStore((state) => state.selectedTariffId);

  const selectedPrepaidStartTime = usePermitStore(
    (state) => state.selectedPrepaidStartDate
  );
  useFocusEffect(
    React.useCallback(() => {
      usePermitStore
        .getState()
        .setBlockCompanybenefitInEdit('PURCHASE_PREPAID');

      return () => {
        usePermitStore.getState().setBlockCompanybenefitInEdit('');
      };
    }, [])
  );
  const prepaidStartDate = checkPrepaidOrPermitTicketDates(
    purchasedPrepaidTickets,
    selectedParkzonePmcUnion,
    selectedVehicle,
    permitStartDate,
    selectedSegment
  );

  const { customer } = useContext(AccountContext);
  const {
    name = '',
    commercialZoneView,
    countryCode,
  } = parkingZone?.data?.parkZone || {};

  const customerSegment = {
    uid: selectedSegment,
    countryCode,
  } as SelectedSegment;
  const isUserRestricted = useAuthStore((state) => state.accountRestricted);

  const endTimeForPrepaidTicket = (): Date => {
    if (isItemPredefinedTicket(selectedParkzonePmcUnion)) {
      const ticketDuration = parseISO8601Duration(
        selectedParkzonePmcUnion.duration as string
      );

      const endDate = new Date(selectedPrepaidStartTime ?? Date.now());
      endDate.setDate(endDate.getDate() + (ticketDuration ?? 0));

      return endDate;
    }
    return new Date();
  };

  const confirmCreatePurchase = async () => {
    if (isUserRestricted) {
      return useUiStore.getState().setSheetModal('accountRestricted');
    }
    if (isItemPredefinedTicket(selectedParkzonePmcUnion)) {
      usePrepaidTicketStore.getState().setConfirmPrepaidTicketData({
        cardId: paymentMethod?.cardId || '',
        corporateAccountUid: getCAbenefit(paymentMethod),
        predefinedTicketUid: selectedParkzonePmcUnion?.uid ?? '',
        customerEmail: customer.contactEmail || '',
        parkingZoneUid: selectedParkingZoneId || '',
        licensePlate: selectedVehicle?.licensePlate?.plateText,
        customerSegment: customerSegment,
        vehicleName: selectedVehicle?.name || '',
        type: 'PREPAID',
        startDate: new Date(prepaidStartDate),
        parkzoneLocation: commercialZoneView?.name || name || '',
        prepaidName: selectedParkzonePmcUnion?.name ?? '',
        validFrom: selectedPrepaidStartTime ?? new Date(),
        validTo: endTimeForPrepaidTicket(),
        countryCode,
        zoneCode: commercialZoneView?.zoneCode,
        price: selectedParkzonePmcUnion?.price,
        serviceFee: selectedParkzonePmcUnion?.serviceFee,
      });
      useUiStore.getState().setSheetModal('confirmPrepaidPurchase');
      return;
    }
  };

  const buyButtonText = () => {
    return t('parking.prepaidTickets.buyPrepaidTitle');
  };

  return (
    <FooterButtonContainer pb={0}>
      <VerticalDistributedContainer>
        <FooterButtonContainer pb={0} prPl={10}>
          <VehicleSelectDropDown />
          <PaymentCardDropDown
            parkingZoneCountryCode={parkingZoneCountryCode}
          />
        </FooterButtonContainer>
        <FooterButtonContainer>
          <Button
            disabled={!licensePlate?.plateText || isEmpty(paymentMethod)}
            size="l"
            onPress={confirmCreatePurchase}
            text={buyButtonText()}
            {...testIdentifiers('prepaid-buy-screen-buy-button')}
          />
        </FooterButtonContainer>
      </VerticalDistributedContainer>
    </FooterButtonContainer>
  );
};

export default BuyPermitButton;
